import { FOR_YOU, TABS_CONFIG } from '@/src/homepage/constants/tabConfig';
import { getPromotedEventsOptions } from '@/src/homepage/containers/BrowseContainer/promotedUtils';
import { filterUnactionableEvents } from '@/src/homepage/shared/utils/filterUnactionableEvents';
import { Location } from '@/src/homepage/types';
import {
    DestinationEvent,
    FormattedEvent,
    transformDestinationEvent,
} from '@eventbrite/consumer-search.event-renderer';
import { sdkRequest } from '@eventbrite/legacy-infra.http';
import { useQuery } from '@tanstack/react-query';
import { getLastRecentSearchTerms, getLastRecentViewsList } from '../utils/utils';

const PROMOTED_EVENTS_TIMEOUT_MS = 3000;

/**
 * Fetches the promoted events for the For-you tab.
 *
 * Ads are deliberately not cached and not refetched in the background: every response is a fresh auction whose
 * impressions get billed, so re-running it on window focus would churn the feed and log impressions the user never saw.
 */
export const usePromotedEvents = ({
    location,
    enabled,
}: {
    location: Location;
    enabled: boolean;
}) =>
    useQuery({
        queryKey: ['for-you-promoted-events', location.placeId, location.isOnline],
        queryFn: () => fetchPromotedEvents(location),
        staleTime: 0,
        cacheTime: 0,
        retry: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        enabled: enabled && Boolean(location.placeId || location.isOnline),
    });

async function fetchPromotedEvents(location: Location): Promise<FormattedEvent[]> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), PROMOTED_EVENTS_TIMEOUT_MS);

    try {
        const response = await sdkRequest(
            '/promoted/events/',
            {
                ...getPromotedEventsOptions(location, TABS_CONFIG[FOR_YOU], {
                    recentSearches: getLastRecentSearchTerms(10),
                    recentViews: getLastRecentViewsList(10),
                }),
                signal: controller.signal,
            },
            '/home',
        );

        // The endpoint answers with a bare array of raw (snake_case) events; anything else means no fill.
        if (!Array.isArray(response)) {
            return [];
        }

        // Drop ads the user could not act on, matching what EventsBucket already filters out on the other tabs,
        // then camelCase them so they slot into the FormattedEvent buckets the For-you feed renders.
        return filterUnactionableEvents(response as DestinationEvent[]).map(
            transformDestinationEvent,
        );
    } finally {
        clearTimeout(timeoutId);
    }
}
